import utils from "../../../utils/utils";

export function pickField(row, ...keys) {
  if (!row) return "";
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

/** Legacy clinical dates: DD/MM/YYYY */
export function toApiDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : utils.parseDateInput(value);
  if (!d || Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

export function parseUiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  return utils.parseDateInput(value);
}

export function displayOrDash(value) {
  const text = String(value ?? "").trim();
  if (!text || text.toLowerCase() === "not available" || text === "—") return "—";
  return text;
}

export function snomedCodeOf(row) {
  return String(
    pickField(
      row,
      "ConceptId",
      "conceptId",
      "SnomedCode",
      "snomedCode",
      "SenomedCode",
      "Snomed",
      "snomed",
      "SnomedId",
      "snomedId",
    ) || "",
  ).trim();
}

export function snomedTermOf(row) {
  return String(
    pickField(
      row,
      "Term",
      "term",
      "SnomedTerm",
      "snomedTerm",
      "SenomedTerm",
      "Name",
      "name",
      "label",
    ) || "",
  ).trim();
}

export function calcBmi(heightCm, weightKg) {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) return "";
  const meters = h / 100;
  return (w / (meters * meters)).toFixed(2);
}

export const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Resolved", label: "Resolved" },
];

function auditNorm(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "—" || text.toLowerCase() === "not available") return "";
  return text;
}

function pushAuditChange(changes, fieldId, label, oldVal, newVal) {
  const oldValue = auditNorm(oldVal);
  const newValue = auditNorm(newVal);
  if (oldValue === newValue) return;
  changes.push({
    fieldType: "Text",
    fieldId,
    label,
    oldValue: oldValue || "—",
    newValue: newValue || "—",
  });
}

function isApplicableFlag(row) {
  const raw = pickField(row, "IsApplicable");
  const key = String(raw ?? "").trim().toLowerCase();
  return key === "1" || key === "true" || key === "yes";
}

/**
 * Build ChangedFormFields for clinical condition add/update
 * (Medical / Surgical / Family / Allergy).
 * Update: only fields that actually changed (old → new).
 */
export function buildConditionAuditFields(section, oldRow, form) {
  const changes = [];
  const isUpdate = Boolean(oldRow);
  const onsetLabel = section?.onsetLabel || "Onset date";
  const newNA = Boolean(form?.notAvailable);
  const newTerm = form?.snomed?.term || "";
  const newStatus = newNA ? "" : form?.status || "";
  const newOnset = newNA ? "" : toApiDate(form?.onset) || "";
  const newResolution =
    newNA || form?.status !== "Resolved"
      ? ""
      : toApiDate(form?.resolution) || "";
  const newRelationship = form?.relationship || "";

  if (!isUpdate) {
    pushAuditChange(changes, "SnomedTerm", "Condition", "", newTerm);
    if (section?.key === "family") {
      pushAuditChange(changes, "Relationship", "Relationship", "", newRelationship);
    }
    pushAuditChange(
      changes,
      "IsApplicable",
      "Not available",
      "",
      newNA ? "Yes" : "No",
    );
    if (!newNA) {
      pushAuditChange(changes, "Status", "Status", "", newStatus);
      pushAuditChange(changes, "OnSetDate", onsetLabel, "", newOnset);
      if (form?.status === "Resolved") {
        pushAuditChange(
          changes,
          "ResolutionDate",
          "Resolution date",
          "",
          newResolution,
        );
      }
    }
    return changes;
  }

  const oldNA = isApplicableFlag(oldRow);
  const oldTerm = pickField(oldRow, "SnomedTerm");
  const oldStatus = oldNA ? "" : pickField(oldRow, "Status");
  const oldOnset = oldNA
    ? ""
    : pickField(oldRow, "DateOfOnSet", "OnSetDate", "LKODate");
  const oldResolution = oldNA
    ? ""
    : pickField(oldRow, "DateOfResolution", "ResolutionDate");
  const oldRelationship = pickField(
    oldRow,
    "RelationShip",
    "Relationship",
  );

  pushAuditChange(changes, "SnomedTerm", "Condition", oldTerm, newTerm);
  if (section?.key === "family") {
    pushAuditChange(
      changes,
      "Relationship",
      "Relationship",
      oldRelationship,
      newRelationship,
    );
  }
  pushAuditChange(
    changes,
    "IsApplicable",
    "Not available",
    oldNA ? "Yes" : "No",
    newNA ? "Yes" : "No",
  );
  pushAuditChange(changes, "Status", "Status", oldStatus, newStatus);
  pushAuditChange(changes, "OnSetDate", onsetLabel, oldOnset, newOnset);
  pushAuditChange(
    changes,
    "ResolutionDate",
    "Resolution date",
    oldResolution,
    newResolution,
  );
  return changes;
}

export function buildPhysicalAuditFields(oldRow, form) {
  const changes = [];
  const isUpdate = Boolean(oldRow);
  const next = {
    age: String(form?.age ?? "").trim(),
    gender: String(form?.gender ?? "").trim(),
    height: String(form?.height ?? "").trim(),
    weight: String(form?.weight ?? "").trim(),
    bmi: String(form?.bmi ?? "").trim(),
  };
  if (!isUpdate) {
    pushAuditChange(changes, "Age", "Age", "", next.age);
    pushAuditChange(changes, "Gender", "Gender", "", next.gender);
    pushAuditChange(changes, "Height", "Height (cm)", "", next.height);
    pushAuditChange(changes, "Weight", "Weight (kg)", "", next.weight);
    pushAuditChange(changes, "BMI", "BMI", "", next.bmi);
    return changes;
  }
  pushAuditChange(
    changes,
    "Age",
    "Age",
    pickField(oldRow, "Age"),
    next.age,
  );
  pushAuditChange(
    changes,
    "Gender",
    "Gender",
    pickField(oldRow, "Gender"),
    next.gender,
  );
  pushAuditChange(
    changes,
    "Height",
    "Height (cm)",
    pickField(oldRow, "Height"),
    next.height,
  );
  pushAuditChange(
    changes,
    "Weight",
    "Weight (kg)",
    pickField(oldRow, "Weight", "weight"),
    next.weight,
  );
  pushAuditChange(
    changes,
    "BMI",
    "BMI",
    pickField(oldRow, "BMI"),
    next.bmi,
  );
  return changes;
}

export function buildMedicationAuditFields(oldRow, form) {
  const changes = [];
  const isUpdate = Boolean(oldRow);
  const next = {
    name: form?.medication?.term || "",
    dose: form?.dose || "",
    unit: form?.unit?.term || "",
    frequency: form?.frequency?.term || "",
    route: form?.route?.term || "",
    indication: form?.indication || "",
    start: toApiDate(form?.startDate) || "",
    end: toApiDate(form?.endDate) || "",
  };
  if (!isUpdate) {
    pushAuditChange(changes, "MedicationName", "Medication", "", next.name);
    pushAuditChange(changes, "Dose", "Dose", "", next.dose);
    pushAuditChange(changes, "Unit", "Unit", "", next.unit);
    pushAuditChange(changes, "Frequency", "Frequency", "", next.frequency);
    pushAuditChange(changes, "Route", "Route", "", next.route);
    pushAuditChange(changes, "Indication", "Indication", "", next.indication);
    pushAuditChange(changes, "StartDate", "Start date", "", next.start);
    pushAuditChange(changes, "ResolutionDate", "End date", "", next.end);
    return changes;
  }
  pushAuditChange(
    changes,
    "MedicationName",
    "Medication",
    pickField(oldRow, "MedicationName"),
    next.name,
  );
  pushAuditChange(changes, "Dose", "Dose", pickField(oldRow, "Dose"), next.dose);
  pushAuditChange(changes, "Unit", "Unit", pickField(oldRow, "Unit"), next.unit);
  pushAuditChange(
    changes,
    "Frequency",
    "Frequency",
    pickField(oldRow, "Frequency"),
    next.frequency,
  );
  pushAuditChange(
    changes,
    "Route",
    "Route",
    pickField(oldRow, "Route"),
    next.route,
  );
  pushAuditChange(
    changes,
    "Indication",
    "Indication",
    pickField(oldRow, "Indication"),
    next.indication,
  );
  pushAuditChange(
    changes,
    "StartDate",
    "Start date",
    pickField(oldRow, "StartDate"),
    next.start,
  );
  pushAuditChange(
    changes,
    "ResolutionDate",
    "End date",
    pickField(oldRow, "ResolutionDate"),
    next.end,
  );
  return changes;
}

export function toChangedFormFieldsJson(changes) {
  if (!Array.isArray(changes) || !changes.length) return null;
  try {
    return JSON.stringify(changes);
  } catch {
    return null;
  }
}

export const CLINICAL_SECTIONS = [
  {
    key: "physical",
    title: "Physical measurements",
    idKey: "PhysicalMeasurementHistoryId",
  },
  {
    key: "medical",
    title: "Medical history",
    idKey: "MedicalHistoryId",
    termType: "MedicalHistory",
    recentTermType: "Medical",
  },
  {
    key: "surgical",
    title: "Surgical history",
    idKey: "SurgicalHistoryId",
    termType: "SurgicalHistory",
    recentTermType: "Surgical",
  },
  {
    key: "family",
    title: "Family history",
    idKey: "FamilyHistoryId",
    termType: "FamilyHistory",
    recentTermType: "Family",
  },
  {
    key: "allergy",
    title: "Allergies",
    idKey: "AllergyHistoryId",
    termType: "Allergy",
    recentTermType: "Allergy",
    onsetLabel: "LKO date",
  },
  {
    key: "medication",
    title: "Medication",
    idKey: "MedicationHistoryId",
  },
];
